import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from './layout/TopBar';
import BottomNav from './BottomNav';
import Phone from './Phone';
import { C } from './theme';
import { fmtCurrency, fmtNum, useDrillAuth } from './drillHelpers';
import { exportPeriodFor } from '../utils/reportPeriod';
import { StaffShimmer, Bone } from './Shimmer';
import { useDesktopTopBar } from '../context/DesktopLayoutContext';
import { useIsDesktop } from './reports/reportTokens';
import SectionError from './reports/SectionError';
import SectionEmpty from './reports/SectionEmpty';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchLiveStaffActiveClockingRequest, fetchLiveStaffActiveClockingV2Request } from '../redux/reports/clickHouse/clickHouseActions';
import type { OrderSource } from '../redux/reports/clickHouse/clickHouseApi';
import { getLiveStaffActiveClockingV2Api } from '../redux/reports/clickHouse/clickHouseApi';
import type { LiveStaffActiveClockingItem, LiveStaffActiveClockingV2Item } from '../interface/clickHouseInterface';
import { todayStr, yesterdayStr, getStartEndFromFilter } from '../services/reportsApi';
import { useStoreTimeZone } from '../utils/dateTimeUtils';
import { getCurrencySymbol, moneyHeader } from '../utils/currency';
import { formatMonetaryExport } from '../utils/reportExport';
import { formatMoneyForExport } from '../utils/currency';
import { DataTableCard, SectionCard, type Col } from './reports/components/DataTableCard';

// Active Staff drill (mobile): both Today and historical are driven exclusively by the
// /sales/live/v2/staff-active-clocking redux state — historical fetches the SAME endpoint
// with startDate/endDate (SSE-17), so the drill always matches the Active Staff KPI.
const DrillStaff = ({ onBack, initialDateFilter, initialDate, orderSource }: { onBack?: () => void; initialDateFilter?: string; initialDate?: string; orderSource?: OrderSource } = {} as any) => {
    const isEmbedded = !!onBack;
    const navigate = useNavigate();
    const isDesktop = useIsDesktop();

    useEffect(() => {
        if (!isEmbedded && isDesktop) navigate('/reports/snapshot?drill=staff', { replace: true });
    }, [isDesktop, navigate, isEmbedded]);
    const setTopBarProps = useDesktopTopBar();
    const { locationId, selectedDate: drillDate, dateFilter: drillDateFilter } = useDrillAuth();
    const dispatch = useAppDispatch();
    const liveStaffActiveClocking: LiveStaffActiveClockingItem[] = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingSuccess);
    const liveStaffActiveClockingLoading: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingLoading);
    const liveStaffActiveClockingFailure: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingFailure);
    // Staff Roster — /sales/live/v2/staff-active-clocking-v2 (paginated/searchable/sortable, any date range)
    const staffRosterV2Items: LiveStaffActiveClockingV2Item[] = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2Items);
    const staffRosterV2Loading: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2Loading);
    const staffRosterV2Failure: boolean = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2Failure);
    const staffRosterV2TotalElements: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2TotalElements);
    const staffRosterV2TotalPages: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2TotalPages);
    const staffRosterV2StaffWorked: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2StaffWorked);
    const staffRosterV2ActiveCount: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2ActiveCount);
    const staffRosterV2BreakCount: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2BreakCount);
    const staffRosterV2TotalWorkedHours: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2TotalWorkedHours);
    const staffRosterV2AvgSalesPerStaff: number = useAppSelector((s) => s.clickHouse.liveStaffActiveClockingV2AvgSalesPerStaff);
    const [staffRosterV2Page, setStaffRosterV2Page] = useState(1);
    const [staffRosterV2PageSize, setStaffRosterV2PageSize] = useState(10);
    const [staffRosterV2Search, setStaffRosterV2Search] = useState('');
    const [staffRosterV2DebouncedSearch, setStaffRosterV2DebouncedSearch] = useState('');
    const [staffRosterV2SortBy, setStaffRosterV2SortBy] = useState('date');
    const [staffRosterV2SortAsc, setStaffRosterV2SortAsc] = useState(false);
    const storeTz = useStoreTimeZone();
    const today = useMemo(() => todayStr(storeTz), [storeTz]);
    const yesterday = useMemo(() => yesterdayStr(storeTz), [storeTz]);
    const [selectedDate, setSelectedDate] = useState(initialDate ?? drillDate ?? today);
    const [activeDateFilter, setActiveDateFilter] = useState(initialDateFilter ?? drillDateFilter ?? 'Today');
    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDateFilter, today, yesterday);
        return range || { startDate: selectedDate, endDate: selectedDate };
    }, [activeDateFilter, selectedDate, today, yesterday]);

    const exportDateRangeLabel = exportPeriodFor(activeDateFilter, dateRange)?.label ?? activeDateFilter;

    const isToday = activeDateFilter === 'Today';
    // Historical passes startDate/endDate to the SAME endpoint; Today omits them (live path).
    const chDates: { startDate?: string; endDate?: string } = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };

    // Staff Roster (v2) — reset paging on date change, debounce search, refetch on any param change.
    useEffect(() => { setStaffRosterV2Page(1); }, [dateRange.startDate, dateRange.endDate]);
    useEffect(() => {
        const t = setTimeout(() => setStaffRosterV2DebouncedSearch(staffRosterV2Search), 300);
        return () => clearTimeout(t);
    }, [staffRosterV2Search]);
    useEffect(() => { setStaffRosterV2Page(1); }, [staffRosterV2DebouncedSearch, staffRosterV2SortBy, staffRosterV2SortAsc]);
    useEffect(() => {
        if (!locationId) return;
        dispatch(fetchLiveStaffActiveClockingV2Request({
            locationId,
            ...chDates,
            search: staffRosterV2DebouncedSearch,
            page: staffRosterV2Page,
            size: staffRosterV2PageSize,
            sortBy: staffRosterV2SortBy,
            sortAsc: staffRosterV2SortAsc,
            orderSource,
        }));
    }, [locationId, isToday, dateRange.startDate, dateRange.endDate, staffRosterV2Page, staffRosterV2PageSize, staffRosterV2DebouncedSearch, staffRosterV2SortBy, staffRosterV2SortAsc, orderSource, dispatch]);

    const handleDateFilterChange = useCallback((opt: string) => {
        setActiveDateFilter(opt);
        if (opt === 'Today') setSelectedDate(today);
        else if (opt === 'Yesterday') setSelectedDate(yesterday);
    }, [today, yesterday]);

    useEffect(() => {
        if (isEmbedded) return;
        setTopBarProps({
            title: 'Staff Worked',
            onBack: () => navigate('/reports/snapshot'),
            dateFilters: { value: activeDateFilter, onChange: handleDateFilterChange },
        });
    }, [setTopBarProps, activeDateFilter, handleDateFilterChange, navigate, isEmbedded]);

    useEffect(() => {
        if (!locationId) return;
        // Embedded: the parent SnapshotScreen already dispatched this fetch (Today + historical)
        // with the current date range and channel filter — don't fire a duplicate request.
        if (isEmbedded) return;
        dispatch(fetchLiveStaffActiveClockingRequest(locationId, orderSource, chDates.startDate, chDates.endDate));
    }, [locationId, isEmbedded, isToday, dateRange.startDate, dateRange.endDate, orderSource, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    // staff-active-clocking `totalHours` comes as "H:MM" (e.g. "0:12") — plain
    // Number() yields NaN, so parse it as H + M/60 before other formats.
    const parseHours = (v: any) => {
        if (v == null) return 0;
        if (typeof v === 'number') return v;
        const str = String(v).trim();
        const colon = str.match(/^(\d+):(\d{1,2})$/);
        if (colon) return Number(colon[1]) + Number(colon[2]) / 60;
        const hMatch = str.match(/(\d+)\s*hrs?/i);
        const mMatch = str.match(/(\d+)\s*mins?/i);
        return (hMatch ? Number(hMatch[1]) : 0) + (mMatch ? Number(mMatch[1]) / 60 : 0);
    };

    // Derive everything from /sales/live/v2/staff-active-clocking redux only.
    const isStatusActive = (s: string | null | undefined) => String(s ?? '').trim().toLowerCase() === 'active';
    const isStatusBreak = (s: string | null | undefined) => String(s ?? '').trim().toLowerCase() === 'break';
    const activeCount = liveStaffActiveClocking.reduce((n, it) => n + (isStatusActive(it.currentStatus) ? 1 : 0), 0);
    const onBreakCount = liveStaffActiveClocking.reduce((n, it) => n + (isStatusBreak(it.currentStatus) ? 1 : 0), 0);
    // LBR-11: the "Active" figure mirrors the parent Active Staff KPI = total headcount
    // (every clocking row in the response), not just the 'active'-status subset.
    const totalStaffCount = liveStaffActiveClocking.length;
    const totalHoursDecimal = liveStaffActiveClocking.reduce((s, it) => s + parseHours(it.totalHours), 0);
    const netSalesSum = liveStaffActiveClocking.reduce((s, it) => s + (Number(it.netSales) || 0), 0);
    const avgSalesPerStaff = liveStaffActiveClocking.length > 0
        ? netSalesSum / liveStaffActiveClocking.length
        : 0;

    const formatStatus = (s: string | null | undefined) => {
        const v = String(s ?? '').trim();
        if (!v) return '—';
        return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
    };
    const statusColor = (s: string | null | undefined) => {
        if (isStatusActive(s)) return C.green;
        if (isStatusBreak(s)) return C.orange;
        return C.t3;
    };
    // clockOut arrives as "InProgress" for open shifts — style like LaborReport's Clock-Out
    const isClockOutInProgress = (v: string | null | undefined) => String(v ?? '').replace(/\s+/g, '').toLowerCase() === 'inprogress';
    const renderClockOut = (v: string | null | undefined) => isClockOutInProgress(v)
        ? <span style={{ color: C.green, fontStyle: 'italic' }}>In Progress</span>
        : (v ?? '—');
    // breakHours "0h 0m" → dash, same as LaborReport's Working Hours Log
    const isZeroDuration = (v: string | null | undefined) => {
        const nums = String(v ?? '').match(/\d+/g);
        return !nums || nums.every((n) => Number(n) === 0);
    };
    const renderBreakHours = (v: string | null | undefined) => isZeroDuration(v)
        ? <span style={{ color: C.t2 }}>—</span>
        : v;
    // Zero worked hours on a completed shift (clock-in/out within a minute) → ⚠ -, same as LaborReport
    const isZeroWorkedAnomaly = (r: LiveStaffActiveClockingV2Item) => isZeroDuration(r.workedHours) && r.clockOut != null && !isClockOutInProgress(r.clockOut);
    const renderWorkedHours = (r: LiveStaffActiveClockingV2Item) => isZeroWorkedAnomaly(r)
        ? <span style={{ color: C.orange, fontWeight: 600 }}>⚠ -</span>
        : (r.workedHours ?? '—');

    const totalHoursLabel = `${totalHoursDecimal.toFixed(1)}h`;
    const subTitle = liveStaffActiveClockingLoading ? null : `${totalStaffCount} active · ${onBreakCount} on break · ${totalHoursLabel} total`;

    const kpis = [
        { l: 'Active', v: liveStaffActiveClockingLoading ? <Bone w={40} h={16} r={4} /> : liveStaffActiveClockingFailure ? '--' : fmtNum(totalStaffCount), c: C.green },
        { l: 'On Break', v: liveStaffActiveClockingLoading ? <Bone w={40} h={16} r={4} /> : liveStaffActiveClockingFailure ? '--' : fmtNum(onBreakCount), c: C.orange },
        { l: 'Total Hours', v: liveStaffActiveClockingLoading ? <Bone w={60} h={16} r={4} /> : liveStaffActiveClockingFailure ? '--' : totalHoursLabel, c: C.t1 },
        { l: 'Avg Sales/Staff', v: liveStaffActiveClockingLoading ? <Bone w={60} h={16} r={4} /> : liveStaffActiveClockingFailure ? '--' : fmtCurrency(avgSalesPerStaff), c: C.t1 },
    ];

    // Staff Roster (Active Staff KPI drilldown) — unified across Today + historical via
    // /sales/live/v2/staff-active-clocking-v2 (paginated/searchable/sortable, any date range).
    // Table UI reuses the shared DataTableCard (same component as LaborReport's Working Hours Log).
    const rosterLoadingInitial = staffRosterV2Loading && staffRosterV2Items.length === 0;
    const rosterTotalHoursLabel = `${staffRosterV2TotalWorkedHours.toFixed(2)}h`;

    const rosterColumns: Col[] = [
        { key: 'date', label: 'Date', sortKey: 'date', value: (r: any) => r.date ?? '', render: (r: any) => r.date ?? '—' },
        { key: 'name', label: 'Employee Name', sortKey: 'employeeName', value: (r: any) => r.employeeName ?? '', render: (r: any) => <span style={{ color: C.accent, fontWeight: 600 }}>{r.employeeName?.trim() || '—'}</span> },
        { key: 'in', label: 'Clock-In', sortKey: 'clockIn', value: (r: any) => r.clockIn ?? '', render: (r: any) => r.clockIn ?? '—' },
        { key: 'out', label: 'Clock-Out', sortKey: 'clockOut', value: (r: any) => r.clockOut ?? '', render: (r: any) => renderClockOut(r.clockOut) },
        { key: 'worked', label: 'Worked Hours', align: 'right', sortKey: 'workedHours', value: (r: any) => r.workedHours ?? '', render: (r: any) => renderWorkedHours(r) },
        { key: 'break', label: 'Break Hours', align: 'right', sortKey: 'breakHours', value: (r: any) => r.breakHours ?? '', render: (r: any) => renderBreakHours(r.breakHours) },
        { key: 'status', label: 'Status', sortKey: 'status', value: (r: any) => r.status ?? '', render: (r: any) => <span style={{ color: statusColor(r.status), fontWeight: 600 }}>{formatStatus(r.status)}</span> },
        { key: 'sales', label: 'Sales', align: 'right', sortKey: 'netSales', value: (r: any) => Number(r.netSales) || 0, render: (r: any) => fmtCurrency(Number(r.netSales) || 0) },
    ];

    const rosterMobileCard = (r: any) => (
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '10px 12px', boxShadow: C.shadow }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>{r.employeeName?.trim() || '—'}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 10, color: C.t3 }}>{r.date ?? '—'}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: statusColor(r.status) }}>{formatStatus(r.status)}</span>
                </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: C.t1, padding: '3px 8px', borderRadius: 4, background: C.hover }}>
                    <span style={{ color: C.t2, fontWeight: 500 }}>Clock-In: </span>{r.clockIn || '—'}
                </span>
                <span style={{ fontSize: 10, fontWeight: 600, color: C.t1, padding: '3px 8px', borderRadius: 4, background: C.hover }}>
                    <span style={{ color: C.t2, fontWeight: 500 }}>Clock-Out: </span>{isClockOutInProgress(r.clockOut) ? <span style={{ color: C.green, fontStyle: 'italic' }}>In Progress</span> : (r.clockOut || '—')}
                </span>
                <span style={{ fontSize: 10, fontWeight: 600, color: C.t1, padding: '3px 8px', borderRadius: 4, background: C.hover }}>
                    <span style={{ color: C.t2, fontWeight: 500 }}>Worked: </span>{isZeroWorkedAnomaly(r) ? <span style={{ color: C.orange, fontWeight: 600 }}>⚠ -</span> : (r.workedHours || '—')}
                </span>
                <span style={{ fontSize: 10, fontWeight: 600, color: C.t1, padding: '3px 8px', borderRadius: 4, background: C.hover }}>
                    <span style={{ color: C.t2, fontWeight: 500 }}>Break: </span>{isZeroDuration(r.breakHours) ? '—' : r.breakHours}
                </span>
                <span style={{ fontSize: 10, fontWeight: 600, color: C.t1, padding: '3px 8px', borderRadius: 4, background: C.hover }}>
                    <span style={{ color: C.t2, fontWeight: 500 }}>Sales: </span>{fmtCurrency(Number(r.netSales) || 0)}
                </span>
            </div>
        </div>
    );

    const inner = rosterLoadingInitial ? <StaffShimmer /> : (<>
        <div style={{ fontSize: 10, color: C.t3, padding: '0 16px 4px' }}>{`${fmtNum(staffRosterV2StaffWorked)} staff worked · ${rosterTotalHoursLabel} total`}</div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {[
                        { l: 'Active', v: staffRosterV2Failure ? '0' : fmtNum(staffRosterV2ActiveCount), c: C.green },
                        { l: 'On Break', v: staffRosterV2Failure ? '0' : fmtNum(staffRosterV2BreakCount), c: C.orange },
                        { l: 'Total Hours', v: staffRosterV2Failure ? '--' : rosterTotalHoursLabel, c: C.t1 },
                        { l: 'Avg Sales/Staff', v: staffRosterV2Failure ? fmtCurrency(0) : fmtCurrency(staffRosterV2AvgSalesPerStaff), c: C.t1 },
                    ].map((k, i) => (
                        <div key={i} style={{ minWidth: 0, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '10px 8px', textAlign: 'center', boxShadow: C.shadow }}>
                            <div style={{ fontSize: 9, fontWeight: 600, color: C.t3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.l}</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: k.c, marginTop: 4 }}>{k.v}</div>
                        </div>
                    ))}
                </div>
                {staffRosterV2Failure ? (
                    <SectionCard title="Staff Roster">
                        <SectionError
                            kpiName="staff roster"
                            onRetry={() => dispatch(fetchLiveStaffActiveClockingV2Request({ locationId, ...chDates, search: staffRosterV2DebouncedSearch, page: staffRosterV2Page, size: staffRosterV2PageSize, sortBy: staffRosterV2SortBy, sortAsc: staffRosterV2SortAsc, orderSource }))}
                            minHeight={120}
                        />
                    </SectionCard>
                ) : (
                    <DataTableCard
                        id="drill-staff-roster"
                        title="Staff Roster"
                        columns={rosterColumns}
                        rows={staffRosterV2Items}
                        mobileCard={rosterMobileCard}
                        searchValue={(r: any) => r.employeeName}
                        loading={staffRosterV2Loading}
                        serverPagination={{
                            totalElements: staffRosterV2TotalElements,
                            page: staffRosterV2Page,
                            pageSize: staffRosterV2PageSize,
                            onPageChange: (page: number, pageSize: number) => { setStaffRosterV2Page(page); setStaffRosterV2PageSize(pageSize); },
                        }}
                        onSearch={(v: string) => setStaffRosterV2Search(v)}
                        searchTerm={staffRosterV2Search}
                        serverSort={{
                            sortBy: staffRosterV2SortBy,
                            sortDir: staffRosterV2SortAsc ? 'asc' : 'desc',
                            onSortChange: (sortBy: string, sortDir: 'asc' | 'desc') => { setStaffRosterV2SortBy(sortBy); setStaffRosterV2SortAsc(sortDir === 'asc'); },
                        }}
                        exportConfig={{
                            title: 'Staff Roster',
                            filename: 'snapshot-staff-roster',
                            dateRangeLabel: exportDateRangeLabel,
                            buildExport: async (_format, opts) => {
                                const targetLocationId = opts?.locationId ?? locationId;
                                let total = staffRosterV2TotalElements || 1;
                                if (opts?.locationId) {
                                    const probe: any = await getLiveStaffActiveClockingV2Api({ locationId: targetLocationId, ...chDates, search: staffRosterV2DebouncedSearch, page: 1, size: 1, sortBy: staffRosterV2SortBy, sortAsc: staffRosterV2SortAsc, orderSource });
                                    total = probe?.totalElements || 1;
                                }
                                const res: any = await getLiveStaffActiveClockingV2Api({ locationId: targetLocationId, ...chDates, search: staffRosterV2DebouncedSearch, page: 1, size: total, sortBy: staffRosterV2SortBy, sortAsc: staffRosterV2SortAsc, orderSource });
                                const list: any[] = Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []);
                                return {
                                    headers: ['Date', 'Employee Name', 'Clock-In', 'Clock-Out', 'Worked Hours', 'Break Hours', 'Status', 'Sales'],
                                    rows: list.map((s: any) => [
                                        s.date ?? '—',
                                        s.employeeName ?? '—',
                                        s.clockIn ?? '—',
                                        isClockOutInProgress(s.clockOut) ? 'In Progress' : (s.clockOut ?? '—'),
                                        s.workedHours ?? '—',
                                        s.breakHours ?? '—',
                                        formatStatus(s.status),
                                        formatMoneyForExport(Number(s.netSales) || 0),
                                    ]),
                                };
                            },
                        }}
                    />
                )}
        </div>
    </>);

    if (isEmbedded) return <>{inner}</>;
    return (
        <Phone>
            <TopBar variant="mobile" />
            {inner}
            <BottomNav />
        </Phone>
    );
};

export default DrillStaff;
